/**
 * HelpFooter Component
 *
 * Reusable help/support footer for wizard steps.
 * Provides consistent styling for "Need help? Contact Support" links.
 */

import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../shared/Icon';

export interface HelpFooterProps {
  /** Custom message text (default: "Need more help?") */
  message?: string;
  /** Custom link text (default: "Contact Support") */
  linkText?: string;
  /** Custom link URL (default: Hawkin support URL) */
  linkUrl?: string;
  /** Custom icon name (default: "help_outline") */
  iconName?: string;
}

/**
 * HelpFooter component
 */
export const HelpFooter: React.FC<HelpFooterProps> = ({
  message = 'Need more help?',
  linkText = 'Contact Support',
  linkUrl = 'https://www.hawkindynamics.com/support',
  iconName = 'help_outline',
}: HelpFooterProps): React.ReactElement => {
  return (
    <HelpSection>
      <Icon name={iconName} size='small' />
      <HelpText>
        {message}{' '}
        <HelpLink href={linkUrl} target='_blank' rel='noopener noreferrer'>
          {linkText}
        </HelpLink>
      </HelpText>
    </HelpSection>
  );
};

// Styled Components

const HelpSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: var(--color-surface-1);
  border-radius: 10px;
  color: var(--color-text);
`;

const HelpText = styled.span`
  font-size: 13px;
  color: var(--color-text);
`;

const HelpLink = styled.a`
  color: var(--color-accent-light);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default HelpFooter;
